import {
  fromSnapshot,
  type ModelClass,
  type SnapshotInOf,
  type SnapshotOutOf,
} from "mobx-keystone";
import { getStoreConfig } from "../../../config";
import { CreationError } from "../error";
import { inferModelType } from "./ref";
import type { TModel } from "./t-model";
import type { ModelSnapshotData, StorageFetchOpts, TStore } from "./t-store";

export type TStoreInit<M extends TModel, S extends TStore<M, any, any>> = {
  [K in keyof S["$"] as K extends keyof TStore<any, any, any>
    ? never
    : K]: K extends keyof TStore<any, any, any>
    ? never
    : SnapshotInOf<S["$"][K]>;
} & Partial<ModelSnapshotData<M>>;

export function createTStoreFactory<
  M extends TModel,
  S extends TStore<M, any, any>,
>(Store: ModelClass<S>) {
  return (
    incomingData?: TStoreInit<M, S>,
    opts?: { skipStorageFetch?: StorageFetchOpts },
  ) => createTStore<M, S>(Store, incomingData, opts);
}

/**
 * While you can pass any store that extends TStore, you'll have to specify the generic params explicitly for now. Some bad type-golf might be needed to fix this (something to do with some extend infer on the return of ExtendedModel(TStore<TModel>)) so we're keeping it as is for now
 *
 * @example
 * ```ts
 * // check src/mobx/core/invoice/store.ts
 * export const createInvoiceStore = () => createTStore<InvoiceModel, InvoiceStore>(InvoiceStore);
 * ```
 * @param Store the store class instance
 * @param defaultData ...dont use this just yet. we need to better type it
 * @returns {Promise<S>} the store instance
 */
export async function createTStore<
  M extends TModel,
  S extends TStore<M, any, any>,
>(
  Store: ModelClass<S>,
  incomingData?: TStoreInit<M, S>,
  opts?: {
    skipStorageFetch?: StorageFetchOpts;
  },
): Promise<S> {
  // A little bit of "Just trust me bro" going on here
  const storeType = (Store as unknown as { $modelType: string }).$modelType;
  const modelType = inferModelType(storeType);

  const storageAdapter = getStoreConfig().storageAdapter;

  console.debug(
    `Fetching stored data for store [${storeType}] from ${storageAdapter.type}`,
  );
  try {
    const [localData, hidden, selected, lastSync] = await Promise.all([
      opts?.skipStorageFetch?._data
        ? ([] as SnapshotOutOf<M>[])
        : storageAdapter.getModels<SnapshotOutOf<M>>(modelType),
      opts?.skipStorageFetch?._hidden
        ? ([] as string[])
        : storageAdapter.get<string[]>(storeType, "hidden-ids"),
      opts?.skipStorageFetch?._selected
        ? ([] as string[])
        : storageAdapter.get<string[]>(storeType, "selected-ids"),
      opts?.skipStorageFetch?.lastSync
        ? null
        : storageAdapter.get<number>(storeType, "last-sync"),
    ]);

    console.debug(
      `Creating store [${storeType}] with ${localData.length} local items from storage.`,
    );

    const { _data, _hidden, _selected, ...etc } = incomingData ?? {
      _data: [],
      _hidden: [],
      _selected: [],
    };
    const store = fromSnapshot<typeof Store>(Store, {
      _data: [],
      lastSync: (
        incomingData?.lastSync ?? new Date(lastSync ?? 0)
      ).toISOString(),
      _hidden: null,
      _selected: null,
      loading: false,
      syncing: false,
      ...etc,
    } as SnapshotInOf<S>);

    const reconciledDataMap = new Map<string, SnapshotInOf<M>>();
    if (incomingData && incomingData._data) {
      incomingData._data.forEach((d) =>
        reconciledDataMap.set(d.id as string, d),
      );
      console.log(
        `Reconciling incoming data with local data for store [${storeType}]... ${reconciledDataMap.size} items from incoming data, ${localData.length} items from local storage.`,
      );

      // If default data exists and we didn't skip fetching data from storage, we need to reconcile the two sets of data
      if (
        reconciledDataMap.size !== 0 &&
        opts?.skipStorageFetch?._data !== false &&
        incomingData._data?.length
      ) {
        for (const item of localData) {
          if (item.id && reconciledDataMap.has(item.id)) {
            const defaultItem = reconciledDataMap.get(item.id);
            reconciledDataMap.set(item.id, {
              ...item,
              ...defaultItem,
            });
          }
        }
      }
    } else {
      localData.forEach((d) => reconciledDataMap.set(d.id as string, d));
    }

    store.setRaw([...reconciledDataMap.values()]);

    if (hidden) {
      hidden.forEach((id) => {
        if (reconciledDataMap.has(id)) {
          store.hide(id);
        }
      });
    }

    if (selected) {
      selected.forEach((id) => {
        if (reconciledDataMap.has(id)) {
          store.select(id);
        }
      });
    }

    store.initialized = true;
    console.log(
      `Store [${store.$modelType}] created with ${store.data.length} items.`,
    );
    store.creationError = false;

    return store;
  } catch (e) {
    window.console.error(e);
  }

  const defaultStore = new Store({
    _data: [],
    // ...defaultData,
    loading: incomingData?.loading ?? false,
    syncing: incomingData?.syncing ?? false,
    lastSync: new Date(),
  });

  defaultStore.errors.push(
    new CreationError("Failed to create store from storage or incoming data"),
  );

  defaultStore.initialized = true;
  defaultStore.creationError = true;

  return defaultStore;
}
